import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Lock, Eye, EyeOff } from 'lucide-react';

const PAYPAL_CLIENT_ID = 'AcQEOvDGXukNwBzCSHPWzOeNZkXsDUgFs0OtlRAiL7lTV36yzkKWl6q6e-XKDQoLh5_Iv6DqqHdiiR2D';
const PLAN_ID = 'P-23549989EG548980WNGMEEMI';

// Access code for this private page - change this to your preferred code
const ACCESS_CODE = 'FWD2026';

const FortWorthDentalPayment = () => {
  const paypalRef = useRef(null);
  const buttonRenderedRef = useRef(false);
  const [sdkReady, setSdkReady] = useState(false);
  const [subscriptionComplete, setSubscriptionComplete] = useState(false);
  const [subscriptionId, setSubscriptionId] = useState(null);
  
  // Access control state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessCode, setAccessCode] = useState('');
  const [showCode, setShowCode] = useState(false);
  const [error, setError] = useState('');
  
  // Check if already authenticated (persisted in session)
  useEffect(() => {
    const savedAuth = sessionStorage.getItem('fwd_payment_auth');
    if (savedAuth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);
  
  const handleAccessSubmit = (e) => {
    e.preventDefault();
    if (accessCode.toUpperCase() === ACCESS_CODE) {
      setIsAuthenticated(true);
      setError('');
      sessionStorage.setItem('fwd_payment_auth', 'true');
    } else {
      setError('Invalid access code. Please try again.');
      setAccessCode('');
    }
  };

  // Load PayPal SDK only after authentication
  useEffect(() => {
    if (!isAuthenticated) return;
    
    // Check if SDK is already loaded
    if (window.paypal) {
      setSdkReady(true);
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector('script[src*="paypal.com/sdk/js"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => setSdkReady(true));
      return;
    }

    // Load the SDK
    const script = document.createElement('script');
    script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&vault=true&intent=subscription`;
    script.setAttribute('data-sdk-integration-source', 'button-factory');
    script.async = true;
    script.onload = () => setSdkReady(true);
    script.onerror = () => console.error('Failed to load PayPal SDK');
    document.body.appendChild(script);

    return () => {
      // Don't remove the script on unmount to prevent re-loading issues
    };
  }, [isAuthenticated]);

  // Render PayPal button once SDK is ready
  useEffect(() => {
    if (!sdkReady || !window.paypal || !paypalRef.current || buttonRenderedRef.current) {
      return;
    }

    // Mark as rendered to prevent double-render in Strict Mode
    buttonRenderedRef.current = true;

    // Clear container before rendering
    paypalRef.current.innerHTML = '';

    window.paypal.Buttons({
      style: {
        shape: 'rect',
        color: 'gold',
        layout: 'vertical',
        label: 'subscribe'
      },
      createSubscription: function(data, actions) {
        return actions.subscription.create({
          plan_id: PLAN_ID
        });
      },
      onApprove: function(data) {
        setSubscriptionId(data.subscriptionID);
        setSubscriptionComplete(true);
      },
      onError: function(err) {
        console.error('PayPal error:', err);
      }
    }).render(paypalRef.current);
  }, [sdkReady]);

  // Access Code Entry Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 py-12">
        <Helmet>
          <title>Fort Worth Dental - Private Access</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        
        <div className="max-w-md mx-auto px-4">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-100 rounded-full mb-4">
                <Lock className="w-8 h-8 text-teal-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">
                Private Access
              </h1>
              <p className="text-gray-600">
                Enter your access code to view your subscription page
              </p>
            </div>
            
            <form onSubmit={handleAccessSubmit} className="space-y-4">
              <div>
                <label htmlFor="accessCode" className="block text-sm font-medium text-gray-700 mb-1">
                  Access Code
                </label>
                <div className="relative">
                  <input
                    type={showCode ? 'text' : 'password'}
                    id="accessCode"
                    data-testid="access-code-input"
                    value={accessCode}
                    onChange={(e) => setAccessCode(e.target.value)}
                    placeholder="Enter your code"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 pr-12"
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCode(!showCode)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    aria-label={showCode ? 'Hide code' : 'Show code'}
                  >
                    {showCode ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}
              
              <button
                type="submit"
                data-testid="access-submit-btn"
                className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
              >
                Access My Account
              </button>
            </form>
            
            <p className="mt-6 text-center text-sm text-gray-500">
              Don't have an access code? Contact your account manager.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Authenticated - Show Payment Page
  return (
    <div className="min-h-screen bg-gray-100 py-12">
      <Helmet>
        <title>Fort Worth Dental - Monthly Subscription</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-4">
              Monthly Subscription
            </h1>
            <p className="text-gray-600 text-lg">
              Fortworthdental.com
            </p>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Citation Posting & Validation Services
            </h2>
            <ul className="space-y-2 text-gray-600">
              <li className="flex items-center">
                <span className="text-teal-500 mr-2">✓</span>
                Business Listing Management
              </li>
              <li className="flex items-center">
                <span className="text-teal-500 mr-2">✓</span>
                Citation Posting & Updates
              </li>
              <li className="flex items-center">
                <span className="text-teal-500 mr-2">✓</span>
                Directory Validation & Monitoring
              </li>
              <li className="flex items-center">
                <span className="text-teal-500 mr-2">✓</span>
                NAP Consistency Management
              </li>
            </ul>
          </div>
          
          {subscriptionComplete ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
              <div className="text-green-600 text-5xl mb-4">✓</div>
              <h3 className="text-xl font-semibold text-green-800 mb-2">
                Subscription Successful!
              </h3>
              <p className="text-green-700">
                Your subscription ID: <strong>{subscriptionId}</strong>
              </p>
              <p className="text-green-600 mt-2 text-sm">
                Thank you for your subscription. You will receive a confirmation email shortly.
              </p>
            </div>
          ) : (
            <div className="flex justify-center">
              <div className="w-full max-w-md">
                {!sdkReady ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
                    <span className="ml-3 text-gray-600">Loading payment options...</span>
                  </div>
                ) : (
                  <div ref={paypalRef} data-testid="paypal-button-container"></div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FortWorthDentalPayment;
